import { ExtensionType } from '@mangovoice/types'
import type { ExtensionInstance } from '@mangovoice/types'

import type { CreateExtensionPayload } from '../types/api.types'

/**
 * Builds link to Grafana call metrics for an extension. Returns empty string when not configured.
 */
export function getCallMetricsUrl(
  grafanaBaseUrl: string | undefined,
  extensionUuid: string,
): string {
  if (!grafanaBaseUrl || !extensionUuid) return ''
  const base = grafanaBaseUrl.replace(/\/$/, '')
  return `${base}?var-extension_uuid=${encodeURIComponent(extensionUuid)}`
}

export function extensionBelongsToPbx(
  extension: Pick<ExtensionInstance, 'pbx'> | null | undefined,
  pbxId: number,
): boolean {
  // `?.` despite the non-optional type: a payload without pbx must not throw.
  return extension?.pbx?.id === pbxId
}

/**
 * URL slug for type-specific extension edit routes.
 * Switchboard (DIAL_PLAN) uses switchboard edit route, not this path.
 */
export const EXTENSION_TYPE_EDIT_SLUG: Record<
  Exclude<ExtensionType, ExtensionType.DIAL_PLAN>,
  string
> = {
  [ExtensionType.LINE]: 'line',
  [ExtensionType.RING_GROUP]: 'ring-group',
  [ExtensionType.CALL_QUEUE]: 'call-queue',
  [ExtensionType.CONFERENCE_ROOM]: 'conference-room',
  [ExtensionType.VIRTUAL_FAX]: 'virtual-fax',
  [ExtensionType.SHARED]: 'shared-voicemail',
}

/** Edit-route slug for a type, or undefined for switchboards (own route). */
export const getEditSlugForExtensionType = (
  type: ExtensionType,
): string | undefined =>
  (EXTENSION_TYPE_EDIT_SLUG as Partial<Record<ExtensionType, string>>)[type]

/**
 * Human-readable labels for extension types (Admin2 parity).
 */
export const EXTENSION_TYPE_LABELS: Record<ExtensionType, string> = {
  [ExtensionType.LINE]: 'Line',
  [ExtensionType.RING_GROUP]: 'Ring Group',
  [ExtensionType.VIRTUAL_FAX]: 'Virtual Fax Machine',
  [ExtensionType.CONFERENCE_ROOM]: 'Conference Room',
  [ExtensionType.DIAL_PLAN]: 'Switchboard',
  [ExtensionType.SHARED]: 'Shared Voicemail Box',
  [ExtensionType.CALL_QUEUE]: 'Call Queue',
}

export const getExtensionTypeLabel = (type: ExtensionType): string =>
  EXTENSION_TYPE_LABELS[type] ?? type

// Extension types selectable as a call queue timeout/escape destination (A1 parity).
export const CALL_QUEUE_DESTINATION_TYPES: ExtensionType[] = [
  ExtensionType.LINE,
  ExtensionType.RING_GROUP,
  ExtensionType.CALL_QUEUE,
  ExtensionType.SHARED,
  ExtensionType.DIAL_PLAN,
]

/** Human-readable label by type edit URL slug (for placeholder breadcrumb/title). */
export const TYPE_SLUG_TO_LABEL: Record<string, string> = {
  line: EXTENSION_TYPE_LABELS[ExtensionType.LINE],
  'ring-group': EXTENSION_TYPE_LABELS[ExtensionType.RING_GROUP],
  'call-queue': EXTENSION_TYPE_LABELS[ExtensionType.CALL_QUEUE],
  'conference-room': EXTENSION_TYPE_LABELS[ExtensionType.CONFERENCE_ROOM],
  'virtual-fax': EXTENSION_TYPE_LABELS[ExtensionType.VIRTUAL_FAX],
  'shared-voicemail': EXTENSION_TYPE_LABELS[ExtensionType.SHARED],
}

/**
 * Map type slug (used in edit routes) to the list page view query param.
 */
export const TYPE_SLUG_TO_VIEW_MODE: Record<string, string> = {
  line: 'lines',
  'ring-group': 'ring-groups',
  'call-queue': 'call-queues',
  'conference-room': 'conference-rooms',
  'virtual-fax': 'virtual-fax',
  'shared-voicemail': 'shared-voicemail',
}

/** Breadcrumb / nav label for the list view corresponding to a type slug. */
export const TYPE_SLUG_TO_LIST_LABEL: Record<string, string> = {
  line: 'Lines',
  'ring-group': 'Ring groups',
  'call-queue': 'Call queues',
  'conference-room': 'Conference rooms',
  'virtual-fax': 'Virtual fax',
  'shared-voicemail': 'Shared Voicemail',
}

/**
 * Ordering options for Extensions list (server-side sort).
 */
export const EXTENSION_ORDERING_OPTIONS: { value: string; label: string }[] = [
  { value: '-updated_at', label: 'Last updated (newest first)' },
  { value: 'updated_at', label: 'Last updated (oldest first)' },
  { value: '-created_at', label: 'Created (newest first)' },
  { value: 'created_at', label: 'Created (oldest first)' },
  { value: 'name', label: 'Name (A\u2013Z)' },
  { value: '-name', label: 'Name (Z\u2013A)' },
  { value: 'number', label: 'Number (asc)' },
  { value: '-number', label: 'Number (desc)' },
]

/** Search placeholder when viewing Lines list (Admin2 parity). */
export const LINES_SEARCH_PLACEHOLDER =
  'Search by name, number, assigned user, or location'

/** Line status filter options (Admin2 parity). Default to Active. */
export const LINE_STATUS_FILTER_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
] as const
export type LineStatusFilterValue =
  (typeof LINE_STATUS_FILTER_OPTIONS)[number]['value']

/**
 * View modes for "other" extension list views.
 */
export type OtherExtensionsViewMode =
  | 'ring-groups'
  | 'call-queues'
  | 'conference-rooms'
  | 'virtual-fax'
  | 'shared-voicemail'

export const OTHER_EXTENSIONS_VIEW_MODES: {
  viewMode: OtherExtensionsViewMode
  label: string
}[] = [
  { viewMode: 'ring-groups', label: 'Ring groups' },
  { viewMode: 'call-queues', label: 'Call queues' },
  { viewMode: 'conference-rooms', label: 'Conference rooms' },
  { viewMode: 'virtual-fax', label: 'Virtual fax' },
  { viewMode: 'shared-voicemail', label: 'Shared Voicemail' },
]

/** Map view mode to API type filter for list. */
export const OTHER_VIEW_MODE_TO_EXTENSION_TYPE: Record<
  OtherExtensionsViewMode,
  ExtensionType
> = {
  'ring-groups': ExtensionType.RING_GROUP,
  'call-queues': ExtensionType.CALL_QUEUE,
  'conference-rooms': ExtensionType.CONFERENCE_ROOM,
  'virtual-fax': ExtensionType.VIRTUAL_FAX,
  'shared-voicemail': ExtensionType.SHARED,
}

export type ExtensionsListViewMode = 'all' | 'lines' | OtherExtensionsViewMode

export const DEFAULT_EXTENSIONS_LIST_ORDERING = 'name'

/** Unified extensions list default (legacy Admin2 / GOV-169). */
export const ALL_EXTENSIONS_LIST_ORDERING = '-number'

export function isOtherExtensionsViewMode(
  value: string | null | undefined,
): value is OtherExtensionsViewMode {
  if (value == null) return false
  return OTHER_EXTENSIONS_VIEW_MODES.some((mode) => mode.viewMode === value)
}

/**
 * Resolves the extensions list view from the URL `view` query param.
 * When missing or invalid, defaults to unified `all` (Admin2 Voice System parity).
 */
export function resolveExtensionsListViewMode(
  viewParam?: string | null,
): ExtensionsListViewMode {
  if (viewParam === 'all' || viewParam === 'lines') return viewParam
  if (isOtherExtensionsViewMode(viewParam)) return viewParam
  return 'all'
}

export function getInitialOrderingForViewMode(
  viewMode: ExtensionsListViewMode,
): string {
  return viewMode === 'all'
    ? ALL_EXTENSIONS_LIST_ORDERING
    : DEFAULT_EXTENSIONS_LIST_ORDERING
}

/**
 * Voicemail time zone options for line edit. Matches legacy admin values, plus
 * IANA identifiers for zones legacy admin has no label for.
 */
export const VOICEMAIL_TIMEZONE_OPTIONS: { value: string; label: string }[] = [
  { value: 'Eastern Time (US & Canada)', label: 'Eastern Time' },
  { value: 'Central Time (US & Canada)', label: 'Central Time' },
  { value: 'Mountain Time (US & Canada)', label: 'Mountain Time' },
  { value: 'Pacific Time (US & Canada)', label: 'Pacific Time' },
  {
    value: 'America/Vancouver',
    label: 'Pacific Time - British Columbia (PCT)',
  },
  { value: 'Arizona', label: 'Arizona' },
  { value: 'Alaska', label: 'Alaska' },
  { value: 'Hawaii', label: 'Hawaii' },
  { value: 'Indiana (East)', label: 'Indiana (East)' },
  { value: 'Atlantic Time (Canada)', label: 'Atlantic Time (Canada)' },
  { value: 'Greenwich Mean Time (UK)', label: 'Greenwich Mean Time (UK)' },
  { value: 'Puerto Rico', label: 'Puerto Rico' },
]

/** Voicemail greeting type options for line edit. */
export const VOICEMAIL_GREETING_OPTIONS: { value: string; label: string }[] = [
  { value: 'system', label: 'System default' },
  { value: 'custom', label: 'Custom greeting' },
]

/** Find-me mode options for line edit. Legacy values are 0/1/2 (Off/On/Ring All). */
export const FINDME_MODE_OPTIONS: { value: string; label: string }[] = [
  { value: '0', label: 'Off' },
  { value: '1', label: 'On' },
  { value: '2', label: 'Ring All' },
]

/** Local presence options for line edit / mass edit. */
export const LOCAL_PRESENCE_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: 'Off' },
  { value: 1, label: 'Group 1' },
  { value: 2, label: 'Group 2' },
  { value: 3, label: 'Group 3' },
  { value: 4, label: 'Group 4' },
  { value: 5, label: 'Group 5' },
  { value: 6, label: 'Group 6' },
]

/** Preferred codec options for line edit. */
export const PREFERRED_CODEC_OPTIONS: {
  value: string
  label: string
  disabled?: boolean
}[] = [
  {
    value: 'OPUS',
    label: 'Best for slow internet (OPUS) \u2014 Supports: Web Phones, Yealink',
  },
  {
    value: 'PCMU',
    label: 'Standard (PCMU) \u2014 Supports: All phone types',
  },
  {
    value: 'G722',
    label: 'HD Audio (G722) \u2014 Supports: Yealink, Cisco',
  },
  {
    value: 'G729',
    label:
      'DEPRECATED \u2014 Good for slow internet (G729) \u2014 Supports: Yealink, Cisco',
    disabled: true,
  },
]

/** T38 device options for line edit. */
export const T38_DEVICE_OPTIONS: { value: string; label: string }[] = [
  { value: '0', label: 'No' },
  { value: '1', label: 'Yes' },
]

/** Call recording options for line edit (legacy tri-state). */
export const CALL_RECORDING_OPTIONS: { value: string; label: string }[] = [
  { value: '0', label: 'No' },
  { value: '1', label: 'Yes' },
  { value: '2', label: 'System Defined' },
]

/** Ring group edit: ring strategy options. */
export const RING_STRATEGY_OPTIONS: { value: string; label: string }[] = [
  { value: 'Ring All', label: 'Ring All' },
  { value: 'Paging Group', label: 'Paging Group' },
]

/** Ring group edit: legacy member limit. */
export const RING_GROUP_MEMBER_LIMIT = 37

/** Call queue edit: agent ring strategy options (legacy value format). */
export const CALL_QUEUE_AGENT_RING_STRATEGY_OPTIONS: {
  value: string
  label: string
}[] = [
  { value: 'ring-all', label: 'Ring All' },
  { value: 'round-robin', label: 'Round Robin' },
  { value: 'longest-idle-agent', label: 'Longest Idle' },
  { value: 'random', label: 'Random' },
  { value: 'agent-with-least-talk-time', label: 'Least Talk Time' },
  { value: 'agent-with-fewest-calls', label: 'Fewest Calls' },
]

/**
 * Normalizes ring-group strategy from mixed API formats to legacy UI value.
 */
export const normalizeRingGroupStrategy = (value?: string | null): string => {
  if (!value) return 'Ring All'
  const normalized = value.trim().toLowerCase()
  if (normalized === 'paging group' || normalized === 'paging_group') {
    return 'Paging Group'
  }
  return 'Ring All'
}

/**
 * Normalizes call-queue ring strategy from mixed API formats to legacy hyphen format.
 */
export const normalizeCallQueueRingStrategy = (
  value?: string | null,
): string => {
  if (!value) return 'ring-all'
  const normalized = value.trim().toLowerCase().replaceAll('_', '-')
  const match = CALL_QUEUE_AGENT_RING_STRATEGY_OPTIONS.find(
    (opt) => opt.value === normalized,
  )
  return match ? match.value : 'ring-all'
}

/**
 * Normalize mixed find-me values to legacy 0/1/2 semantics.
 */
export const normalizeFindMeMode = (value?: string | number | null): string => {
  if (!value && value !== 0) return '0'
  const normalized = String(value).trim().toLowerCase()
  if (normalized === '1' || normalized === 'on' || normalized === 'sequential')
    return '1'
  if (
    normalized === '2' ||
    normalized === 'ring all' ||
    normalized === 'simultaneous'
  )
    return '2'
  return '0'
}

export const normalizeFindMeDelay = (value?: number | null): number => {
  if (value == null || Number.isNaN(value)) return 0
  const normalized = Math.trunc(value)
  return normalized === 0 ||
    normalized === 5 ||
    normalized === 10 ||
    normalized === 15
    ? normalized
    : 0
}

export const normalizeOptionalPhoneNumber = (value?: string | null): string => {
  const trimmed = (value ?? '').trim()
  if (trimmed === '') return ''
  const hasPlus = trimmed.startsWith('+')
  const digits = trimmed.replace(/[^0-9]/g, '')
  if (digits === '') return ''
  return hasPlus ? `+${digits}` : digits
}

const UK_RESERVED_NUMBERS = new Set([112, 999])

/**
 * Reserved extension number rule.
 * Always-reserved patterns: N11 (211-911), 933, and 59xx+.
 * When `options.isUkEnvironment` is true, UK emergency numbers (112, 999)
 * are also reserved.
 */
export const isReservedExtensionNumber = (
  value: string,
  options?: { isUkEnvironment?: boolean },
): boolean => {
  const normalized = value.trim()
  if (normalized === '') return false
  if (/^([2-9]11|933|59\d{2,})$/.test(normalized)) return true
  if (options?.isUkEnvironment) {
    const num = parseInt(normalized, 10)
    if (!Number.isNaN(num) && UK_RESERVED_NUMBERS.has(num)) return true
  }
  return false
}

/**
 * Compute the next available extension number from existing extensions.
 * Matches Legacy logic: MAX(number) + 1, skip reserved numbers, clamp to [100, 99999].
 */
const RESERVED_NUMBERS = new Set([211, 311, 411, 511, 611, 711, 811, 911, 933])

export const getNextExtensionNumber = (
  sameTypeNumbers: (string | undefined | null)[],
  allUsedNumbers: (string | undefined | null)[],
  fallbackDefault = 100,
  isUkEnvironment = false,
): string => {
  const maxNum = sameTypeNumbers.reduce((max, num) => {
    const n = parseInt(num ?? '', 10)
    return !Number.isNaN(n) && n > max ? n : max
  }, 0)
  let next = maxNum > 0 ? maxNum + 1 : fallbackDefault
  next = Math.max(next, 100)
  const usedSet = new Set(
    allUsedNumbers
      .map((n) => parseInt(n ?? '', 10))
      .filter((n) => !Number.isNaN(n)),
  )
  while (
    (RESERVED_NUMBERS.has(next) ||
      (isUkEnvironment && UK_RESERVED_NUMBERS.has(next)) ||
      usedSet.has(next)) &&
    next <= 99999
  )
    next++
  return String(Math.min(next, 99999))
}

/** Type-specific default starting numbers when no extensions exist (Legacy parity). */
export const EXTENSION_TYPE_DEFAULT_NUMBER: Record<string, number> = {
  line: 101,
  'ring-group': 200,
  'call-queue': 300,
  'conference-room': 400,
  'virtual-fax': 500,
  'shared-voicemail': 600,
}

/**
 * Legacy app_type bit-field mapping: 0=both off, 1=mobile only, 2=webphone only, 3=both on.
 */
export const parseAppTypeFlags = (value?: number | null) => {
  const appType = value ?? 0
  return {
    mobileAppEnabled: appType === 1 || appType === 3,
    webPhoneEnabled: appType === 2 || appType === 3,
  }
}

/**
 * Encode mobile/web toggles into legacy app_type bit-field.
 */
export const encodeAppTypeFlags = (params: {
  mobileAppEnabled: boolean
  webPhoneEnabled: boolean
}): number => {
  if (params.mobileAppEnabled && params.webPhoneEnabled) return 3
  if (params.mobileAppEnabled) return 1
  if (params.webPhoneEnabled) return 2
  return 0
}

/** Default voicemail timezone (Mountain Time) \u2014 matches Legacy behavior. */
export const DEFAULT_VOICEMAIL_TIMEZONE = 'Mountain Time (US & Canada)'

const IANA_TO_LEGACY_TZ: Record<string, string> = {
  'America/New_York': 'Eastern Time (US & Canada)',
  'America/Chicago': 'Central Time (US & Canada)',
  'America/Denver': 'Mountain Time (US & Canada)',
  'America/Los_Angeles': 'Pacific Time (US & Canada)',
  'America/Phoenix': 'Arizona',
  'America/Anchorage': 'Alaska',
  'Pacific/Honolulu': 'Hawaii',
  'America/Toronto': 'Eastern Time (US & Canada)',
  'Europe/London': 'Greenwich Mean Time (UK)',
  'America/Puerto_Rico': 'Puerto Rico',
  'America/Indiana/Indianapolis': 'Indiana (East)',
  'America/Halifax': 'Atlantic Time (Canada)',
  'America/Juneau': 'Alaska',
  'America/Adak': 'Hawaii',
  'US/Eastern': 'Eastern Time (US & Canada)',
  'US/Central': 'Central Time (US & Canada)',
  'US/Mountain': 'Mountain Time (US & Canada)',
  'US/Pacific': 'Pacific Time (US & Canada)',
  'US/Alaska': 'Alaska',
  'US/Hawaii': 'Hawaii',
  'US/East-Indiana': 'Indiana (East)',
  UTC: 'Greenwich Mean Time (UK)',
}

/**
 * Normalize a voicemail_time_zone value read from the API to the legacy
 * format used by VOICEMAIL_TIMEZONE_OPTIONS. Handles IANA identifiers
 * that may have been stored by earlier versions of the new admin.
 * Returns the value unchanged if it already matches a legacy option or
 * isn't recognized.
 */
export const normalizeToLegacyTimezone = (tz: string): string =>
  IANA_TO_LEGACY_TZ[tz] ?? tz

/** Name of the auto-generated "Everyone" user group. */
export const EVERYONE_GROUP_NAME = 'Everyone'

/** Standard toast durations (ms) for consistency across extension pages. */
export const TOAST_DURATION_SUCCESS = 2000
export const TOAST_DURATION_ERROR = 5000

/**
 * Shared validation patterns for extension edit pages.
 *
 * `EMAIL_REGEX` is intentionally written so the literal `.` is the only
 * place a dot can appear before the final segment: the first domain
 * group `[^\s.@]+` excludes `.`, which removes the
 * polynomial-backtracking ambiguity CodeQL flagged when the previous
 * `[^\s@]+\.[^\s@]+` form was reused inside a `.split(',')` loop on
 * untrusted input.
 */
export const EMAIL_REGEX = /^[^\s@]+@[^\s.@]+\.[^\s@]+$/
export const FORWARDING_NUMBER_REGEX = /^\+?[0-9]{1,15}$/

/**
 * Validate an email address list using legacy admin's "Message Email
 * Address" rules.
 *
 * Legacy admin (`Utility_model::validateEmail_regex`) splits the input on
 * commas and runs each segment against an email regex. An empty input is
 * considered valid (the field is optional), and the legacy regex also
 * tolerates empty segments produced by trailing commas. Whitespace
 * surrounding each address is trimmed for forgiveness, but whitespace
 * inside an address still fails (matching the legacy "Make sure there
 * are NO spaces" alert).
 */
export const isValidEmailList = (value: string): boolean => {
  const trimmed = value.trim()
  if (trimmed === '') return true
  const segments = trimmed.split(',')
  for (const segment of segments) {
    const candidate = segment.trim()
    if (candidate === '') continue
    if (!EMAIL_REGEX.test(candidate)) return false
  }
  return true
}

/**
 * Shared CSS overrides for Chakra Switch alignment in edit page forms.
 * Apply via `css={SWITCH_FORM_CSS}` on a parent VStack.
 */
export const SWITCH_FORM_CSS = {
  '& .chakra-switch__root': {
    display: 'inline-flex !important',
    alignItems: 'center !important',
    gap: '12px !important',
    lineHeight: 1.2,
    minHeight: '20px',
  },
  '& .chakra-switch__control': {
    flexShrink: 0,
  },
} as const

/**
 * Builds the create payload for a LINE extension.
 *
 * The backend requires `outbound_external_callerid` on line creation (it
 * rejects the request with "Outbound external caller ID is required for line
 * extensions" otherwise). The line edit page previously created the line with
 * a minimal body and then PATCHed the caller ID in a follow-up request — which
 * broke once the field became required-on-create, because the create call
 * 400'd before the follow-up update could run. Include it in the create body.
 */
export function buildLineCreatePayload(input: {
  name: string
  number: string
  assignedProfileId: number
  locationId: number | null
  outboundExternalCallerId: string | null
}): CreateExtensionPayload {
  return {
    type: ExtensionType.LINE,
    name: input.name,
    number: input.number,
    assigned_voip_profile_id: input.assignedProfileId,
    location_id: input.locationId,
    outbound_external_callerid: input.outboundExternalCallerId,
  }
}
